/**
 * Background decoration component.
 * Displays the branding image in various cropped/positioned styles
 * to create visual harmony across all pages.
 */

import { cn } from "@/lib/utils";

export type DecorationVariant =
  | "fullscreen" // Full background coverage
  | "corner-top-right" // Dashboard, main pages
  | "corner-bottom-left" // Admin pages
  | "stripe-right" // Catalog, search pages
  | "stripe-bottom" // Circulation pages
  | "corner-bottom-right" // AI pages
  | "subtle-center" // Statistics pages
  | "wave-top" // Portal pages
  | "diagonal-slice"; // Acquisition/Periodical pages

interface BackgroundDecorationProps {
  variant: DecorationVariant;
  className?: string;
  /** Opacity override (0-100) */
  opacity?: number;
}

const variantStyles: Record<DecorationVariant, string> = {
  // Fullscreen - covers entire background
  fullscreen: cn("absolute inset-0 w-full h-full", "object-cover object-center"),

  // Top right corner - prominent floating elements
  "corner-top-right": cn(
    "absolute -top-12 -right-12 w-[32rem] h-[32rem]",
    "object-cover object-[80%_20%]",
    "rounded-bl-[100px]",
    "rotate-6"
  ),

  // Bottom left corner - grounded feel
  "corner-bottom-left": cn(
    "absolute -bottom-8 -left-8 w-[28rem] h-[28rem]",
    "object-cover object-[20%_80%]",
    "rounded-tr-[80px]",
    "-rotate-3"
  ),

  // Right stripe - wide vertical slice
  "stripe-right": cn(
    "absolute top-0 -right-4 w-80 h-full",
    "object-cover object-[70%_center]",
    "rounded-l-3xl"
  ),

  // Bottom stripe - horizontal slice
  "stripe-bottom": cn(
    "absolute -bottom-4 left-0 w-full h-56",
    "object-cover object-[center_90%]",
    "rounded-t-3xl"
  ),

  // Bottom right corner - AI/innovation feel (largest)
  "corner-bottom-right": cn(
    "absolute -bottom-16 -right-16 w-[38rem] h-[38rem]",
    "object-cover object-[85%_85%]",
    "rounded-tl-[120px]",
    "rotate-3"
  ),

  // Subtle center - background texture
  "subtle-center": cn(
    "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
    "w-[80%] h-[80%] min-w-[500px] min-h-[500px]",
    "object-cover object-center",
    "rounded-[60px]"
  ),

  // Wave top - organic top decoration
  "wave-top": cn(
    "absolute -top-20 left-1/2 -translate-x-1/2 w-[140%] h-80",
    "object-cover object-[center_10%]",
    "rounded-b-[200px]"
  ),

  // Diagonal slice - dynamic feel
  "diagonal-slice": cn(
    "absolute -top-12 -right-20 w-[36rem] h-[36rem]",
    "object-cover object-[60%_40%]",
    "[clip-path:polygon(25%_0%,100%_0%,100%_100%,0%_75%)]"
  ),
};

const variantOpacity: Record<DecorationVariant, number> = {
  fullscreen: 25,
  "corner-top-right": 18,
  "corner-bottom-left": 15,
  "stripe-right": 16,
  "stripe-bottom": 14,
  "corner-bottom-right": 22,
  "subtle-center": 12,
  "wave-top": 15,
  "diagonal-slice": 18,
};

export function BackgroundDecoration({ variant, className, opacity }: BackgroundDecorationProps) {
  const finalOpacity = opacity ?? variantOpacity[variant];

  return (
    <div
      className={cn(
        "pointer-events-none overflow-hidden select-none",
        "absolute inset-0 z-0",
        className
      )}
      aria-hidden="true"
    >
      <img
        src="/Écosystème flottant du savoir.png"
        alt=""
        className={cn(variantStyles[variant], "transition-opacity duration-500")}
        style={{ opacity: finalOpacity / 100 }}
        loading="lazy"
      />
      {/* Gradient overlay to blend with background - lighter to show more of the image */}
      <div
        className={cn(
          "absolute inset-0",
          "from-background/60 via-background/20 to-background/60 bg-gradient-to-br",
          "dark:from-background/70 dark:via-background/30 dark:to-background/70"
        )}
      />
    </div>
  );
}

/**
 * Get the appropriate decoration variant based on the current route.
 * Using fullscreen for all pages for consistent branding.
 */
export function getDecorationVariant(_pathname: string): DecorationVariant {
  // Use fullscreen for all pages - consistent branding
  return "fullscreen";
}
